import { Link } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import { usePlatformTenants, usePlatformApprovals, usePlatformKpis, usePlatformKpisTrends, type PlatformKpisTrends } from '@/lib/queries';

/**
 * /platform (F87c) — direct port of kollez/pages/superadmin/dashboard.html.
 *   Header + 5 KPIs · left: Placement activity mini-bars + Tenants-to-watch table
 *   right: Systems (health dots) + Admin quick-links (Plans/Invoices/Flags/Audit)
 */
export function PlatformDashboardPage() {
  const tenants = usePlatformTenants();
  const approvals = usePlatformApprovals('pending');
  const kpis = usePlatformKpis();
  const trends = usePlatformKpisTrends('30d');

  const rows = tenants.data?.tenants ?? [];
  const pendingApprovals = kpis.data?.approvals.pending ?? approvals.data?.approvals?.length ?? 0;
  // Step 85 — Tenant.status is UPPERCASE. Old lowercase compare meant every tenant was
  // considered active regardless of DB state.
  const active = rows.filter((t) => t.status !== 'SUSPENDED');
  const suspended = kpis.data?.tenants.suspended ?? rows.filter((t) => t.status === 'SUSPENDED').length;
  const activeCount = kpis.data?.tenants.active ?? active.length;
  const totalTenants = kpis.data?.tenants.total ?? rows.length;
  const drivesTotal = kpis.data?.drives.total ?? 0;
  const offersAccepted = kpis.data?.offers.accepted ?? 0;

  const bars = (heights: number[]) => (
    <div className="mini">
      {heights.map((h, i) => (
        <i key={i} style={{ height: `${h}%` }} />
      ))}
    </div>
  );

  /**
   * Real height-percent series from /platform/kpis/trends. Buckets the last N days
   * into `slots` bins so a 30d window renders as 12 bars of ~2.5 days each; the
   * scale is relative to the tallest bar in this row (so drives + offers don't
   * fight for headroom).
   */
  function series(rows: Array<{ day: string; count: number }> | undefined, slots = 12): number[] {
    if (!rows || rows.length === 0) return Array.from({ length: slots }, () => 0);
    const days = rows.slice(-slots * 3);
    const perBin = Math.max(1, Math.ceil(days.length / slots));
    const bins: number[] = [];
    for (let i = 0; i < slots; i++) {
      const chunk = days.slice(i * perBin, (i + 1) * perBin);
      bins.push(chunk.reduce((n, r) => n + r.count, 0));
    }
    const max = Math.max(1, ...bins);
    return bins.map((n) => Math.max(4, Math.round((n / max) * 100)));
  }
  const t: PlatformKpisTrends | undefined = trends.data;
  const activityLabel = trends.isLoading ? 'Weekly · last 30 days' : 'Daily · last 30 days';

  return (
    <div className="stack">
      <style>{`
        .mini { display: flex; align-items: end; gap: 3px; height: 48px; }
        .mini i { flex: 1; background: rgb(var(--ink-accent) / .7); border-radius: 2px 2px 0 0; }
        .mini i:last-child { background: rgb(var(--ink-accent)); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">{new Date().toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
          <h1 className="h-page">Platform overview</h1>
          <p className="subhead">
            {activeCount} active tenant{activeCount === 1 ? '' : 's'}
            {pendingApprovals > 0 ? ` · ${pendingApprovals} approval${pendingApprovals === 1 ? '' : 's'} waiting` : ' · nothing waiting for approval'}
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/platform/approvals">
            <Icon name="inbox" size={14} />Approvals
            {pendingApprovals > 0 && <span className="badge badge-warning ml-1">{pendingApprovals}</span>}
          </Link>
          <Link className="btn btn-primary" to="/platform/provision"><Icon name="plus" size={14} />Provision tenant</Link>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi">
          <span className="kpi-label">Active tenants</span>
          <span className="kpi-value">{activeCount}</span>
          <span className="kpi-meta">of {totalTenants} total</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Approvals waiting</span>
          <span className="kpi-value">{pendingApprovals}</span>
          <span className="kpi-meta">institutions + companies</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Suspended</span>
          <span className="kpi-value">{suspended}</span>
          <span className="kpi-meta">tenants on hold</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Cluster</span>
          <span className="kpi-value">Mumbai</span>
          <span className="kpi-meta">ap-south-1</span>
        </div>
        <div className="card kpi">
          <span className="kpi-label">Weekly rollup</span>
          <span className="kpi-value">Live</span>
          <span className="kpi-meta">populated every Monday 06:00 IST</span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] items-start">
        <div className="grid gap-5 min-w-0">
          <section className="card card-pad grid gap-3">
            <div className="flex items-baseline justify-between">
              <h2 className="h-section">Placement activity across tenants</h2>
              <span className="helper">{activityLabel}</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <p className="helper m-0">Drives posted</p>
                <b className="h-serif text-2xl">{drivesTotal.toLocaleString('en-IN')}</b>
                {bars(series(t?.drivesCreated))}
              </div>
              <div>
                <p className="helper m-0">Applications · 30d</p>
                <b className="h-serif text-2xl">
                  {(t?.applications.reduce((n, r) => n + r.count, 0) ?? 0).toLocaleString('en-IN')}
                </b>
                {bars(series(t?.applications))}
              </div>
              <div>
                <p className="helper m-0">Offers accepted</p>
                <b className="h-serif text-2xl">{offersAccepted.toLocaleString('en-IN')}</b>
                {bars(series(t?.offersAccepted))}
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="h-section">Tenants to watch</h2>
              <Link className="link text-sm" to="/platform/tenants">All tenants</Link>
            </div>
            <AsyncBoundary
              kind="table"
              inCard
              loading={tenants.isLoading}
              error={tenants.error}
              empty={!tenants.isLoading && rows.length === 0}
              emptyTitle="No tenants yet"
              onRetry={() => void tenants.refetch()}
            >
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Why</th>
                      <th className="hide-mobile">Plan</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 5).map((t) => (
                      <tr key={t._id}>
                        <td className="cell-title">{t.name}</td>
                        <td>
                          <span className={`badge ${t.status === 'SUSPENDED' ? 'badge-danger' : t.subscription ? 'badge-success' : 'badge-warning'}`}>
                            {t.status === 'SUSPENDED' ? 'Suspended' : t.subscription ? 'Active' : 'No plan'}
                          </span>
                          <div className="cell-sub">{t.tenantType} · {t.slug}</div>
                        </td>
                        <td className="hide-mobile">{t.subscription?.planName ?? '—'}</td>
                        <td className="r">
                          <Link className="btn btn-outline btn-sm" to={`/platform/tenants`}>Open</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AsyncBoundary>
          </section>
        </div>

        <div className="grid gap-5 min-w-0">
          <section className="card card-pad grid gap-2">
            <div className="flex items-center justify-between">
              <h2 className="h-section">Systems</h2>
              <Link className="link text-sm" to="/platform/fraud">Fraud queue</Link>
            </div>
            <div className="flex items-center gap-2 text-sm"><span className="dot dot-success" />API responding</div>
            <div className="flex items-center gap-2 text-sm"><span className="dot dot-success" />Notification worker active</div>
            <div className="flex items-center gap-2 text-sm"><span className="dot dot-success" />Nexie AI cap tracked per tenant</div>
          </section>

          <section className="card card-pad grid gap-2">
            <h2 className="h-section">Admin</h2>
            <Link className="sb-link" to="/platform/tenants"><Icon name="layers" size={14} /><span>Tenants</span></Link>
            <Link className="sb-link" to="/platform/approvals"><Icon name="inbox" size={14} /><span>Approvals</span></Link>
            <Link className="sb-link" to="/platform/audit"><Icon name="scroll-text" size={14} /><span>Audit log</span></Link>
            <Link className="sb-link" to="/platform/fraud"><Icon name="toggle-right" size={14} /><span>Fraud queue</span></Link>
            <Link className="sb-link" to="/platform/provision"><Icon name="user-search" size={14} /><span>Provision tenant</span></Link>
            <Link className="sb-link" to="/platform"><Icon name="database" size={14} /><span>Colleges reference</span></Link>
            <Link className="sb-link" to="/platform"><Icon name="receipt" size={14} /><span>Invoices</span></Link>
          </section>
        </div>
      </div>
    </div>
  );
}
